/**
 * FormField + SelectField — one field pattern: label, hint, error next to the
 * field, disabled with its reason.
 *
 * `SelectField` wraps the existing `shared/FilterSelect` (already sanitising and
 * sorting options) instead of starting a parallel select implementation.
 */
import { useId } from "react";
import { AlertCircle } from "lucide-react";
import { FilterSelect } from "@/components/shared/FilterSelect";

export function FormField({
  label,
  htmlFor,
  hint,
  error,
  required = false,
  disabled = false,
  disabledReason,
  className = "",
  testId,
  children,
}) {
  const auto = useId();
  const id = htmlFor || auto;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;

  return (
    <div
      data-testid={testId}
      className={`min-w-0 ${className}`}
      title={disabled && disabledReason ? disabledReason : undefined}
    >
      {label && (
        <label
          htmlFor={id}
          className="block text-label uppercase text-content-muted mb-1.5"
        >
          {label}
          {required && <span className="text-danger ml-0.5" aria-hidden="true">*</span>}
        </label>
      )}
      {typeof children === "function"
        ? children({ id, "aria-describedby": [hintId, errorId].filter(Boolean).join(" ") || undefined, "aria-invalid": error ? true : undefined, disabled })
        : children}
      {hint && !error && (
        <p id={hintId} className="mt-1 text-xs text-content-muted">{hint}</p>
      )}
      {error && (
        <p
          id={errorId}
          role="alert"
          data-testid={testId ? `${testId}-error` : undefined}
          className="mt-1 flex items-start gap-1 text-xs text-danger"
        >
          <AlertCircle className="w-3.5 h-3.5 mt-px shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}
      {disabled && disabledReason && (
        <p
          data-testid={testId ? `${testId}-disabled-reason` : undefined}
          className="mt-1 text-xs text-content-muted"
        >
          {disabledReason}
        </p>
      )}
    </div>
  );
}

/** Labelled select built on the shared FilterSelect. */
export function SelectField({
  label, value, onChange, options, allLabel, hint, error, disabled, disabledReason,
  className = "", testId, ...rest
}) {
  return (
    <FormField
      label={label}
      hint={hint}
      error={error}
      disabled={disabled}
      disabledReason={disabledReason}
      className={className}
      testId={testId}
    >
      {({ id, ...aria }) => (
        <FilterSelect
          id={id}
          size="field"
          width="w-full"
          value={value}
          onChange={onChange}
          options={options}
          placeholder={allLabel}
          disabled={disabled}
          testid={testId ? `${testId}-select` : undefined}
          {...aria}
          {...rest}
        />
      )}
    </FormField>
  );
}

export default FormField;
